import { apiService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import type { AiUsageSummary, GetAiUsageLogsParams, PaginatedAiUsageLogs } from "@/types";

/**
 * `GET /api/ai-usage/summary` and `GET /api/ai-usage/logs`
 * (`components/ai-usage.md`). The contract mirror lives in
 * `src/types/ai-usage.ts` -- this module declares none of it
 * (`conventions/08-api-contracts.md`).
 */

// Deliberately NOT keyed on `selectedBrandId`: AI usage is a cross-organization,
// brand-independent read for platform admins, so `constraints.md § C-16` (which
// covers brand-scoped data) does not apply. Same precedent as
// `landscape-log.api.ts`; a future reader should not "fix" it by adding a brand.
// No `refetchInterval`: the section is read, not watched.
export const aiUsageQueryKeys = {
  all: ["ai-usage"] as const,
  summary: () => [...aiUsageQueryKeys.all, "summary"] as const,
  logs: (params: GetAiUsageLogsParams = {}) => [...aiUsageQueryKeys.all, "logs", params] as const,
};

export const useGetAiUsageSummary = () =>
  useQuery<AiUsageSummary>({
    queryKey: aiUsageQueryKeys.summary(),
    queryFn: () => apiService.get<AiUsageSummary, undefined>("/ai-usage/summary"),
  });

type AiUsageLogsRequestParams = Pick<GetAiUsageLogsParams, "page" | "per_page"> & {
  search?: string;
  model?: string[];
  feature?: string[];
  status?: string[];
};

// Arrays go out as arrays: axios 1.x serializes them as `model[]=a&model[]=b`,
// which is what `ListAiUsageLogsRequest` validates. Never comma-joined. Empty
// arrays and an empty search are left out of the request entirely.
function toRequestParams(params: GetAiUsageLogsParams): AiUsageLogsRequestParams {
  const { search, model, feature, status, page, per_page } = params;
  const out: AiUsageLogsRequestParams = {};
  if (search && search.trim() !== "") out.search = search;
  if (model?.length) out.model = model;
  if (feature?.length) out.feature = feature;
  if (status?.length) out.status = status;
  if (page !== undefined) out.page = page;
  if (per_page !== undefined) out.per_page = per_page;
  return out;
}

export const useGetAiUsageLogs = (params: GetAiUsageLogsParams = {}) =>
  useQuery<PaginatedAiUsageLogs>({
    queryKey: aiUsageQueryKeys.logs(params),
    queryFn: () =>
      apiService.get<PaginatedAiUsageLogs, AiUsageLogsRequestParams>(
        "/ai-usage/logs",
        toRequestParams(params),
      ),
  });
